import { hydrateCategoryButtons } from './categoryButtonsHydrator';
import { cssEscape } from './domUtils';
import { deactivateDynamicDataBindings, loadDynamicDataForView } from './dynamicBindings';
import { hydrateHomeDiscovery, resumeHomeRecommendedGrid, resumeMobileHomeFeeds } from './homeDiscoveryHydrator';
import { deactivateHomeMember, hydrateHomeMemberLayout } from './homeMemberHydrator';
import { deactivateHomeFlywheel, hydrateHomeFlywheel } from './homeRecommendationHydrator';
import { deactivateHomeRecentAndFavorites, HOME_LIBRARY_ITEM_LIMIT, hydrateHomeRecentAndFavorites, reconcileHomeRecent } from './homeRecentHydrator';
import { clearHomeReturnState, homeReturnStateFromHistory, restoreHomeReturnState } from './homeReturnState';
import { clearHomeRecommendedTopics, getHomeRecommendedSession } from './homeRecommendedTopics';
import { refreshHomeState } from './homeStateController';
import type { PrototypeState } from './prototypeHydration';
import type { Root } from './prototypeTypes';

interface ActiveHomeHydration {
	key: string;
	api: PrototypeState['runtime']['api'];
	promise: Promise<void>;
}

const activeHydrations = new WeakMap<object, ActiveHomeHydration>();

interface StableHomeSnapshot {
	key: string;
	api: PrototypeState['runtime']['api'];
	isLoggedIn: boolean;
	/** Every return-anchor-critical Home hydrator settled while Home was active. */
	settled: boolean;
	ready: boolean;
	generation: number;
}

const stableHomeSnapshots = new WeakMap<object, StableHomeSnapshot>();
const homeSnapshotGenerations = new WeakMap<object, number>();

/** Drops the active Home hydration promise when navigation leaves Home. */
export function deactivateHomeViewHydration(root: Root): boolean {
	// A successful Back marks the active Home view to suppress the generic
	// translateY entry animation. Remove it only while leaving Home, never
	// during the active return where removing it would restart that animation.
	root.querySelector<HTMLElement>('#view-home')?.classList.remove('home-return-preserved');
	const snapshot = stableHomeSnapshots.get(root);
	// The original readiness check runs while the hydration promise settles.
	// A final layout mutation can make that instant too early, even though all
	// critical tasks have since finished. Re-evaluate only an already-settled
	// generation at the departure boundary; an in-flight pass remains invalid.
	if (snapshot?.settled === true) snapshot.ready = isHomeSnapshotStable(root, snapshot.isLoggedIn);
	const ready = snapshot?.ready === true;
	// A settled snapshot is intentionally retained for same-viewer Back. An
	// incomplete pass, however, may still resolve after this view is hidden;
	// delete it and advance its generation so that late work cannot turn it
	// into a valid return cache.
	if (!ready) {
		stableHomeSnapshots.delete(root);
		nextHomeSnapshotGeneration(root);
	}
	activeHydrations.delete(root);
	deactivateHomeRecentAndFavorites(root);
	deactivateHomeMember();
	deactivateHomeFlywheel();
	deactivateDynamicDataBindings();
	return ready;
}

export function isHomeViewActive(root: Root): boolean {
	return root.querySelector<HTMLElement>('#view-home')?.classList.contains('active') === true;
}

export function hydrateActiveHome(root: Root, state: PrototypeState): Promise<void> {
	if (!isHomeViewActive(root)) return Promise.resolve();
	const key = state.isLoggedIn && state.runtime.userId
		? `user:${state.runtime.userId}:${state.effectiveAccessPlan}`
		: `visitor:${state.effectiveAccessPlan}`;
	const existing = activeHydrations.get(root);
	if (existing?.key === key && existing.api === state.runtime.api) return existing.promise;
	const returnState = typeof window !== 'undefined' && window.history ? homeReturnStateFromHistory(window.history.state) : null;
	const snapshot = stableHomeSnapshots.get(root);
	const hasStableSnapshot = snapshot?.ready === true && snapshot.key === key && snapshot.api === state.runtime.api;
	// Home departure always clears the For You pager, even when the card that
	// opened Topic lived in a different Home rail. A stable return must restore
	// that cleared section too, otherwise Back from Trending/New leaves a blank
	// For You module farther down the same Home page.
	const canRestoreRecommendation = getHomeRecommendedSession(state.runtime.api) !== null;
	if (returnState && hasStableSnapshot && (returnState.containerId === 'mobileTrendingFeed' || canRestoreRecommendation)) {
		// A Back return is not a normal Home entry. Its source view has already
		// painted this user's hero, Recent, Saved, Trending, and New sections.
		// Replacing any of those with their first-frame skeletons changes the
		// height above the departed card, then makes the return anchor visibly
		// chase the page. Only For You is cleared on departure, so rebuild that
		// one cached pager synchronously and leave every upstream surface stable.
		const preservedCard = returnState.containerId === 'recommended-list'
			? root.querySelector<HTMLElement>(`#recommended-list .topic-card[data-id="${cssEscape(returnState.cardSlug)}"]`)
			: null;
		const canUsePreservedDom = preservedCard !== null && !hasVisibleRecommendedSkeleton(root);
		const restore = isMobileViewport(root) && (returnState.containerId === 'recommended-list' || returnState.containerId === 'mobileTrendingFeed')
			? Promise.resolve(resumeMobileHomeFeeds(root, state.runtime.api))
			: canUsePreservedDom ? Promise.resolve() : hydrateHomeDiscovery(root, state.runtime.api, { returnPreserve: true });
		const promise = restore.then(async () => {
			// Restore the captured viewport and rail anchor before any network work.
			// A silent reconciliation may finish later, but it must never delay the
			// first Back frame behind an API response.
			await restoreHomeReturnState(root);
			// The retained Home snapshot prevents a visual reset on Back, but it
			// must not freeze Recently at its departure state. This reconciliation
			// mutates only the result cards, preserves scrollLeft, and never shows
			// skeletons, so the return anchor remains stable on mobile and desktop.
			if (state.isLoggedIn) void reconcileHomeRecent(root, state.runtime.api);
		}).finally(() => {
			if (activeHydrations.get(root)?.promise === promise) activeHydrations.delete(root);
		});
		activeHydrations.set(root, { key, api: state.runtime.api, promise });
		return promise;
	}
	if (returnState) {
		// A marker is only valid for the exact viewer that finished a complete
		// Home paint. Never let a different account, plan, or partial first
		// load inherit the prior viewer's DOM or recommendation cache.
		if (snapshot && (snapshot.key !== key || snapshot.api !== state.runtime.api)) clearHomeRecommendedTopics();
		clearHomeReturnState();
		window.scrollTo({ top: 0, behavior: 'instant' as ScrollBehavior });
	}
	const snapshotGeneration = nextHomeSnapshotGeneration(root);
	stableHomeSnapshots.set(root, { key, api: state.runtime.api, isLoggedIn: state.isLoggedIn, settled: false, ready: false, generation: snapshotGeneration });

	const home = root.querySelector<HTMLElement>('#view-home');
	// An invalid/incomplete Home return clears member data before Home becomes
	// visible. Keep the structural member first frame through the replacement
	// load; otherwise refreshHomeState switches data-auth to "member" while the
	// cleared Hero is still empty, producing one or more blank frames.
	const retainMemberFirstFrame = state.isLoggedIn && home?.dataset.auth === 'pending';
	hydrateCategoryButtons(root);
	refreshHomeState(root, state);
	if (retainMemberFirstFrame && home) home.dataset.auth = 'pending';
	const api = state.runtime.api;
	// One Home generation owns one Recent snapshot. Both the Recently Opened
	// rail and the Hero consume this exact promise, so a return cannot mix two
	// progress reads and a new viewer/generation can never inherit an old one.
	const recentRequest = state.isLoggedIn ? api.topicRecent({ limit: HOME_LIBRARY_ITEM_LIMIT }) : undefined;
	// Discovery and the personal rails paint independently. They are still
	// included in the return-snapshot contract below, but they must never keep
	// the member first frame on screen after the Hero itself has settled.
	const criticalTasks: Array<Promise<unknown>> = [hydrateHomeDiscovery(root, api, { ignoreReturnSession: true })];
	// Metrics and keyword clouds never establish card positions. Let them finish
	// independently so a slow enhancement cannot turn an otherwise stable Back
	// into a skeleton repaint; their existing generation guards suppress hidden
	// Home writes after deactivateHomeViewHydration().
	void loadDynamicDataForView(root, { view: 'home', plan: state.effectiveAccessPlan }, api).catch(() => undefined);
	if (!state.isLoggedIn && typeof api.listCommunityIdeas === 'function') criticalTasks.push(hydrateHomeFlywheel(root, api));
	if (state.isLoggedIn) {
		criticalTasks.push(hydrateHomeRecentAndFavorites(root, api, recentRequest));
		const memberLayout = hydrateHomeMemberLayout(root, api, recentRequest);
		criticalTasks.push(memberLayout);
		// `data-auth="pending"` owns both the Hero placeholder and the two
		// personal-rail heading placeholders. It is a first-frame guard, not a
		// page-wide loading state: waiting for Trending/Newest/Recent/Saved here
		// made already-painted cards appear underneath a permanent skeleton when
		// one unrelated request was slow, failed, or belonged to a superseded
		// navigation generation. The Hero layout has exactly the inputs that this
		// guard represents (resume + shared recommendation), so release it as soon
		// as that layout settles. Its own safe empty/error states count as settled.
		void memberLayout.then(
			() => releaseMemberFirstFrame(root, key, api, snapshotGeneration),
			() => releaseMemberFirstFrame(root, key, api, snapshotGeneration),
		);
	}
	const promise = Promise.allSettled(criticalTasks).then(async () => {
		await restoreHomeReturnState(root);
		const current = stableHomeSnapshots.get(root);
		if (
			current?.generation === snapshotGeneration
			&& current.key === key
			&& current.api === api
			&& activeHydrations.get(root)?.promise === promise
			&& isHomeViewActive(root)
		) {
			// This aggregate is only the return-snapshot boundary. The member
			// first-frame marker already releases with the Hero layout itself so
			// unrelated discovery/library work cannot hold visible content behind
			// its skeleton. Repeating the guarded call is harmless self-healing.
			if (state.isLoggedIn) releaseMemberFirstFrame(root, key, api, snapshotGeneration);
			current.settled = true;
			current.ready = isHomeSnapshotStable(root, current.isLoggedIn);
		}
	}).finally(() => {
		if (activeHydrations.get(root)?.promise === promise) activeHydrations.delete(root);
	});
	activeHydrations.set(root, { key, api, promise });
	return promise;
}

/**
 * Validates a retained For You grid before Home becomes visible on Back.
 * Returning a different viewer must clear the hidden old grid before its
 * first Home frame; only a complete same-viewer snapshot may keep it alive.
 */
export function prepareHomeReturnFrame(root: Root, state: PrototypeState): boolean {
	const returnState = typeof window !== 'undefined' && window.history ? homeReturnStateFromHistory(window.history.state) : null;
	const key = state.isLoggedIn && state.runtime.userId
		? `user:${state.runtime.userId}:${state.effectiveAccessPlan}`
		: `visitor:${state.effectiveAccessPlan}`;
	const snapshot = stableHomeSnapshots.get(root);
	const list = root.querySelector<HTMLElement>('#recommended-list');
	const trendingList = root.querySelector<HTMLElement>('#mobileTrendingFeed');
	const trustedHome = snapshot?.ready === true
		&& snapshot.key === key
		&& snapshot.api === state.runtime.api
		&& isHomeSnapshotStable(root, snapshot.isLoggedIn);
	if (!returnState) {
		root.querySelector<HTMLElement>('#view-home')?.classList.remove('home-return-preserved');
		if (!trustedHome) clearUntrustedHomeDom(root, state.isLoggedIn);
		return false;
	}
	// Only long mobile feeds retain their DOM. Other Home sources keep their
	// own stable cards and rehydrate For You from the viewer-bound session.
	if (returnState.containerId !== 'recommended-list' && returnState.containerId !== 'mobileTrendingFeed') {
		root.querySelector<HTMLElement>('#view-home')?.classList.remove('home-return-preserved');
		if (!trustedHome) clearUntrustedHomeDom(root, state.isLoggedIn);
		return false;
	}
	const returnList = returnState.containerId === 'mobileTrendingFeed' ? trendingList : list;
	const card = returnList?.querySelector<HTMLElement>(`.topic-card[data-id="${cssEscape(returnState.cardSlug)}"]`);
	const valid = trustedHome
		&& (returnState.containerId === 'mobileTrendingFeed' || getHomeRecommendedSession(state.runtime.api) !== null)
		&& card !== null
		&& (returnState.containerId === 'mobileTrendingFeed' || !hasVisibleRecommendedSkeleton(root));
	if (valid) {
		root.querySelector<HTMLElement>('#view-home')?.classList.add('home-return-preserved');
		return isMobileViewport(root)
			? resumeMobileHomeFeeds(root, state.runtime.api)
			: resumeHomeRecommendedGrid(root, state.runtime.api);
	}
	root.querySelector<HTMLElement>('#view-home')?.classList.remove('home-return-preserved');
	// This list is hidden in Topic but could otherwise become visible for one
	// frame before the normal hydration paints skeletons for a new viewer.
	clearUntrustedHomeDom(root, state.isLoggedIn);
	return false;
}

/** Removes user-specific Home paint before a different viewer can see it. */
function clearUntrustedHomeDom(root: Root, showMemberFirstFrame: boolean): void {
	if (showMemberFirstFrame) root.querySelector<HTMLElement>('#view-home')?.setAttribute('data-auth', 'pending');
	for (const id of ['recommended-list', 'mobileTrendingFeed', 'recent-list', 'favorites-list', 'paidHeroUpNext']) {
		root.querySelector<HTMLElement>(`#${id}`)?.replaceChildren();
	}
	// Do not let a prior member's empty personal-rail headers leak into the
	// incoming member's first frame. Their own settled results reveal each rail.
	for (const id of ['recentSection', 'savedSection']) {
		const section = root.querySelector<HTMLElement>(`#${id}`);
		if (section) section.hidden = true;
	}
	const cloudSlide = root.querySelector<HTMLElement>('#paidHeroCloud');
	cloudSlide?.querySelector<HTMLElement>('.upnext-cloud')?.replaceChildren();
	cloudSlide?.style.removeProperty('background-image');
	cloudSlide?.classList.remove('active');
	root.querySelector<HTMLElement>('#paidHeroUpNext')?.classList.add('active');
	root.querySelector<HTMLElement>('#recentEmpty')?.style.setProperty('display', 'none');
	root.querySelector<HTMLElement>('#favoritesEmpty')?.style.setProperty('display', 'none');
	const title = root.querySelector<HTMLElement>('#paidHeroTitle');
	if (title) title.textContent = 'Your learning desk';
	const description = root.querySelector<HTMLElement>('#paidHeroTopicDesc');
	if (description) description.textContent = '';
	const subtitle = root.querySelector<HTMLElement>('#paidHeroSubtitle');
	if (subtitle) subtitle.textContent = 'Choose a topic to begin.';
	const primaryAction = root.querySelector<HTMLElement>('#paidHeroPrimaryAction');
	if (primaryAction) {
		primaryAction.textContent = 'Browse all topics';
		primaryAction.dataset.protoClick = "navigateToBrowse('all')";
		primaryAction.removeAttribute('aria-label');
	}
	const browseAction = root.querySelector<HTMLElement>('#paidHeroBrowseAction');
	if (browseAction) {
		browseAction.style.display = 'none';
		browseAction.textContent = 'AI Create';
		browseAction.dataset.protoClick = "showView('ai-create');setActiveTab('ai-create')";
	}
	const carouselLabel = root.querySelector<HTMLElement>('#upnextCarouselLabel');
	if (carouselLabel) carouselLabel.textContent = 'Up next';
	for (const control of root.querySelectorAll<HTMLElement>('#upnextPrevBtn, #upnextNextBtn')) control.hidden = true;
	for (const [index, dot] of Array.from(root.querySelectorAll<HTMLElement>('.upnext-dot')).entries()) {
		dot.classList.toggle('active', index === 0);
		dot.hidden = true;
		dot.setAttribute('aria-current', String(index === 0));
	}
	root.querySelector<HTMLElement>('#memberWorkspaceSection')?.setAttribute('aria-busy', 'true');
	for (const metric of root.querySelectorAll<HTMLElement>('[data-bind^="home."]')) metric.textContent = '—';
	clearHomeRecommendedTopics();
}

/**
 * Releases the member-only first frame after the Hero's own layout settles.
 *
 * This deliberately does not require the aggregate Home hydration promise to
 * remain active. That promise also tracks independent discovery and library
 * rails for Back-snapshot eligibility; using it as the only release guard
 * coupled a slow rail to the Hero and left `data-auth="pending"` behind after
 * the visible cards had already painted. The generation/viewer checks retain
 * the important safety property: stale work can never reveal another
 * member's cleared Home DOM, and an explicit session-bootstrap failure still
 * owns its retry state because no logged-in member hydration starts for it.
 */
function releaseMemberFirstFrame(
	root: Root,
	key: string,
	api: PrototypeState['runtime']['api'],
	generation: number,
): void {
	const current = stableHomeSnapshots.get(root);
	if (
		current?.generation !== generation
		|| current.key !== key
		|| current.api !== api
		|| !isHomeViewActive(root)
	) return;
	root.querySelector<HTMLElement>('#memberWorkspaceSection')?.setAttribute('aria-busy', 'false');
	const home = root.querySelector<HTMLElement>('#view-home');
	if (home?.dataset.auth === 'pending') home.dataset.auth = 'member';
}

function nextHomeSnapshotGeneration(root: Root): number {
	const generation = (homeSnapshotGenerations.get(root) ?? 0) + 1;
	homeSnapshotGenerations.set(root, generation);
	return generation;
}

function isHomeSnapshotStable(root: Root, isLoggedIn: boolean): boolean {
	const commonLoading = [
		'#recommended-list .topic-card-skeleton:not([data-return-reserve])',
		'#homeTopicNewestRail .home-topic-skeleton',
		'#featuredSection[aria-busy="true"]',
	];
	// Only the member mobile feed swaps the Trending surface on phones; a guest
	// phone keeps the classic #homeTopicTrendingRail (see usesMobileDiscoveryLayout).
	const discoveryLoading = isMobileViewport(root) && isLoggedIn
		? ['#mobileTrendingFeed .topic-card-skeleton:not([data-return-reserve])']
		: ['#homeTopicTrendingRail .home-topic-skeleton'];
	const layoutLoading = isLoggedIn
		? ['#recent-list .home-recent-skeleton', '#favorites-list .home-saved-skeleton', '#memberWorkspaceSection[aria-busy="true"]', '#paidHeroUpNext .home-upnext-skeleton']
		: ['#flywheelCard .home-flywheel-skeleton', '#flywheelMobileCard .home-flywheel-skeleton'];
	return root.querySelector([...commonLoading, ...discoveryLoading, ...layoutLoading].join(', ')) === null;
}

function isMobileViewport(root: Root): boolean {
	const view = root.ownerDocument?.defaultView ?? (typeof window !== 'undefined' ? window : null);
	return view?.matchMedia?.('(max-width: 640px)').matches ?? (view?.innerWidth ?? 0) <= 640;
}

function hasVisibleRecommendedSkeleton(root: Root): boolean {
	return root.querySelector('#recommended-list .topic-card-skeleton:not([data-return-reserve])') !== null;
}
